/** @format */

import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Autocomplete,
  Grid,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

/**
 * Compute Batch Payslip Component
 * Created: October 30, 2025
 * Purpose: Generate payslips for multiple employees in a batch
 */
const ComputeBatchPayslip = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [postDate, setPostDate] = useState(dayjs());
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [computationResult, setComputationResult] = useState(null);
  const [progress, setProgress] = useState(0);

  const steps = ["Select Batch", "Compute Payslips", "Review & Post"];

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      const response = await axios.get("/payroll/batchPaySlip");
      if (response.data && Array.isArray(response.data)) {
        // Filter only DRAFT batches
        const draftBatches = response.data.filter((b) => b.status === "DRAFT");
        setBatches(draftBatches);
      }
    } catch (err) {
      console.error("Error fetching batches:", err);
    }
  };

  const handleComputeBatch = async () => {
    if (!selectedBatch) {
      setError("Please select a batch");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");
      setProgress(0);

      const payload = {
        batchId: selectedBatch._id,
        postDate: postDate.format("YYYY-MM-DD"),
      };

      // Simulate progress (since backend doesn't provide real-time progress)
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 500);

      const response = await axios.post(
        "/payroll/computeEnhancedBatchPaySlip",
        payload
      );

      clearInterval(progressInterval);
      setProgress(100);

      if (response.data) {
        setComputationResult(response.data);
        setSuccess(
          `Successfully generated ${response.data.payslipsGenerated} payslips!`
        );
        setActiveStep(2);
      }
    } catch (err) {
      setError(err.response?.data || "Error computing batch payslips");
      console.error("Error computing batch:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0 && !selectedBatch) {
      setError("Please select a batch");
      return;
    }
    if (activeStep === 1) {
      handleComputeBatch();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const formatDate = (date) => {
    return dayjs(date).format("DD MMM YYYY");
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Compute Batch Payslip
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
            {success}
          </Alert>
        )}

        {/* Step 1: Select Batch */}
        {activeStep === 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Select Batch
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={batches}
                    getOptionLabel={(option) =>
                      `${option.batchName} (${formatDate(option.fromDate)} - ${formatDate(
                        option.toDate
                      )})`
                    }
                    value={selectedBatch}
                    onChange={(event, newValue) => setSelectedBatch(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Batch"
                        required
                        fullWidth
                        helperText="Only DRAFT batches are shown"
                      />
                    )}
                  />
                </Grid>

                {selectedBatch && (
                  <>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        Period
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(selectedBatch.fromDate)} -{" "}
                        {formatDate(selectedBatch.toDate)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        Department
                      </Typography>
                      <Typography variant="body1">
                        {selectedBatch.departmentDetails?.name || "All Departments"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        Branch
                      </Typography>
                      <Typography variant="body1">
                        {selectedBatch.branchDetails?.nameOfStore || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        Status
                      </Typography>
                      <Chip label={selectedBatch.status} color="default" />
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Compute */}
        {activeStep === 1 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Compute Payslips
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Post Date"
                    value={postDate}
                    onChange={(newValue) => setPostDate(newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info">
                    This will generate payslips for all employees in the selected batch
                    based on their pay templates and attendance records.
                  </Alert>
                </Grid>

                {loading && (
                  <Grid item xs={12}>
                    <Box sx={{ width: "100%" }}>
                      <Typography variant="body2" gutterBottom>
                        Computing payslips... {progress}%
                      </Typography>
                      <LinearProgress variant="determinate" value={progress} />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Review */}
        {activeStep === 2 && computationResult && (
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: 40 }} />
                <Typography variant="h6">Computation Complete!</Typography>
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Batch Name
                  </Typography>
                  <Typography variant="h6">
                    {computationResult.batch?.batchName}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Payslips Generated
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {computationResult.payslipsGenerated}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Total Employees
                  </Typography>
                  <Typography variant="h6">
                    {computationResult.totalEmployees}
                  </Typography>
                </Grid>
              </Grid>

              <Alert severity="success" sx={{ mb: 2 }}>
                All payslips have been generated successfully. The batch status has
                been updated to COMPUTED. You can now review individual payslips and
                post them when ready.
              </Alert>

              <Typography variant="body2" color="text.secondary">
                Next Steps:
              </Typography>
              <ul>
                <li>Review individual payslips in the Payslip List</li>
                <li>Make any necessary adjustments</li>
                <li>Post the batch when ready to finalize</li>
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Navigation Buttons */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
          <Button disabled={activeStep === 0} onClick={handleBack}>
            Back
          </Button>
          <Box>
            {activeStep < steps.length - 1 && (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} />
                  ) : activeStep === 1 ? (
                    <PlayArrowIcon />
                  ) : null
                }
              >
                {activeStep === 1
                  ? loading
                    ? "Computing..."
                    : "Compute Payslips"
                  : "Next"}
              </Button>
            )}
            {activeStep === steps.length - 1 && (
              <Button
                variant="contained"
                color="success"
                onClick={() => (window.location.href = "/userdashboard/payroll/payslips")}
              >
                View Payslips
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default ComputeBatchPayslip;
